import React, { useEffect, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { money } from "@/lib/format";

import {
  ReceiptText,
  Users,
  BookOpen,
  LayoutGrid,
} from "lucide-react";


export const GlobalSearch = ({ open, setOpen }) => {
  const { branchId } = useApp();
  const navigate = useNavigate();

  const [data, setData] = useState({
    orders: [],
    customers: [],
    items: [],
    tables: [],
  });

  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadSearchData = async () => {
      setLoading(true);

      const results = await Promise.allSettled([
        api.orders({
          branch_id: branchId,
          limit: 50,
        }),

        api.customers(),

        api.menuItems({
          branch_id: branchId,
        }),

        api.tables(branchId),
      ]);

      if (cancelled) return;

      const [
        ordersResult,
        customersResult,
        itemsResult,
        tablesResult,
      ] = results;

      setData({
        orders:
          ordersResult.status === "fulfilled"
            ? ordersResult.value?.data || []
            : [],

        customers:
          customersResult.status === "fulfilled"
            ? customersResult.value?.data || []
            : [],

        items:
          itemsResult.status === "fulfilled"
            ? itemsResult.value?.data || []
            : [],

        tables:
          tablesResult.status === "fulfilled"
            ? tablesResult.value?.data || []
            : [],
      });

      setLoading(false);
    };

    loadSearchData();

    return () => {
      cancelled = true;
    };
  }, [open, branchId]);


  const close = () => {
    setOpen(false);
  };


  const go = (path) => {
    close();
    navigate(path);
  };


  const openOrder = (order) => {
    close();

    navigate(
      `/app/orders?orderId=${encodeURIComponent(order.id)}`,
    );
  };


  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
    >

      <CommandInput
        placeholder="Search orders, customers, items, tables…"
        data-testid="global-search-input"
      />


      <CommandList>

        {loading && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            Searching…
          </div>
        )}


        {!loading && (
          <CommandEmpty>
            No results found.
          </CommandEmpty>
        )}


        {/* ORDERS */}
        {!loading && data.orders.length > 0 && (
          <CommandGroup heading="Orders">

            {data.orders.slice(0, 10).map((order) => (
              <CommandItem
                key={order.id}
                value={`order ${order.order_no} ${
                  order.type || ""
                } ${order.customer_name || ""} ${
                  order.table_name || ""
                }`}
                onSelect={() => openOrder(order)}
              >

                <ReceiptText
                  size={15}
                  className="mr-2 text-primary"
                />

                <span>
                  #{order.order_no}
                  {" · "}
                  {(order.type || "")
                    .replaceAll("_", " ")}
                  {" · "}
                  {money(order.total || 0)}
                </span>

              </CommandItem>
            ))}

          </CommandGroup>
        )}


        {/* CUSTOMERS */}
        {!loading && data.customers.length > 0 && (
          <CommandGroup heading="Customers">

            {data.customers.slice(0, 10).map((customer) => (
              <CommandItem
                key={customer.id}
                value={`customer ${
                  customer.name || ""
                } ${customer.phone || ""}`}
                onSelect={() =>
                  go("/app/customers")
                }
              >

                <Users
                  size={15}
                  className="mr-2 text-primary"
                />

                <span>
                  {customer.name || "Unnamed"}
                  {" · "}
                  {customer.phone || "No phone"}
                </span>

              </CommandItem>
            ))}

          </CommandGroup>
        )}


        {/* MENU ITEMS */}
        {!loading && data.items.length > 0 && (
          <CommandGroup heading="Menu Items">

            {data.items.slice(0, 10).map((item) => (
              <CommandItem
                key={item.id}
                value={`menu ${item.name || ""}`}
                onSelect={() =>
                  go("/app/menu")
                }
              >

                <BookOpen
                  size={15}
                  className="mr-2 text-primary"
                />

                <span>
                  {item.name || "Unnamed item"}
                  {" · "}
                  {money(item.price || 0)}
                </span>

              </CommandItem>
            ))}

          </CommandGroup>
        )}


        {/* TABLES */}
        {!loading && data.tables.length > 0 && (
          <CommandGroup heading="Tables">

            {data.tables.slice(0, 10).map((table) => (
              <CommandItem
                key={table.id}
                value={`table ${
                  table.name || ""
                } ${table.floor || ""}`}
                onSelect={() =>
                  go("/app/tables")
                }
              >

                <LayoutGrid
                  size={15}
                  className="mr-2 text-primary"
                />

                <span>
                  {table.name || "Unnamed table"}
                  {" · "}
                  {table.floor || "Floor"}
                </span>

              </CommandItem>
            ))}

          </CommandGroup>
        )}

      </CommandList>

    </CommandDialog>
  );
};